import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [];

  const quickLinks = [
    { name: "Accueil", href: "#hero" },
    { name: "Services", href: "#services" },
    { name: "Qui suis-je ?", href: "#about" },
    { name: "Galerie", href: "#galerie" },
    { name: "Témoignages", href: "#testimonials" },
    { name: "FAQ", href: "#faq" },
    { name: "Ressources", href: "#ressources" },
    { name: "Contact", href: "#contact" }
  ];

  const services = [
    "Suivi de grossesse",
    "Accouchement en plateau technique", 
    "Soins post-partum",
    "Préparation à la naissance",
    "Consultations couples",
    "Soutien allaitement"
  ];

  const legalLinks = [
    { name: "Mentions légales", href: "/mentions-legales" },
    { name: "Politique de confidentialité", href: "/politique-confidentialite" },
    { name: "CGU", href: "/cgu" }
  ];

  return (
    <footer className="bg-sage-900 text-white">

      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Brand Section */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h4 className="font-display text-2xl font-semibold text-warm-200 mb-4">
                  Angèle Rosalie
                </h4>
                <p className="text-sage-300 mb-6 leading-relaxed">
                  Sage-femme libérale passionnée, j'accompagne les femmes et les familles 
                  dans leur parcours de maternité avec bienveillance et expertise.
                </p>
                
                {/* Lien Doctolib */}
                <div className="mt-4">
                  <a
                    href="https://www.doctolib.fr/sage-femme/annecy/angele-rosalie?pid=practice-665411&utm_source=google_appointment_redirect&utm_campaign=gmb&utm_medium=organic&rwg_token=AE37R_iWIak67ZhxFCXZ3-Sqq_0-u2Ki4QPsjXxbWCBQpCTegxlq7Zx0Qgd0JzI-dgdesiX8j0CrP6Ejt1bfGbBNvtBr2FM4nA%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-white text-primary-700 px-4 py-2 rounded-full font-semibold text-sm shadow hover:shadow-md transition"
                  >
                    Rendez-vous Doctolib
                  </a>
                </div>

                {/* Crédit */}
                <p className="text-sage-400 text-xs mt-4">
                  Site réalisé par Souleymane 
                </p>
                <a href="mailto:babatambadou@gmail.com" className="hover:text-primary-300 transition-colors">
                  <p className="text-sage-400 text-xs mt-4">
                    Mail : babatambadou@gmail.com 
                  </p>
                </a>
                <a href="tel:0609476476" className="hover:text-primary-300 transition-colors">
                  <p className="text-sage-400 text-xs mt-4">
                    Portable : 0609476476
                  </p>
                </a>
              </motion.div>
            </div>

            {/* Quick Links */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h5 className="font-semibold text-warm-200 text-lg mb-6">
                  Navigation
                </h5>
                <ul className="space-y-3">
                  {quickLinks.map((link, index) => (
                    <li key={link.name}>
                      <motion.a
                        href={link.href}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 + index * 0.05 }}
                        whileHover={{ x: 5, color: "#ec7a6b" }}
                        viewport={{ once: true }}
                        className="text-sage-300 hover:text-primary-400 transition-all duration-200 block"
                      >
                        {link.name}
                      </motion.a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Services */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h5 className="font-semibold text-warm-200 text-lg mb-6">
                  Services
                </h5>
                <ul className="space-y-3">
                  {services.map((service, index) => (
                    <li key={service}>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.4 + index * 0.05 }}
                        viewport={{ once: true }}
                        className="text-sage-300 flex items-start"
                      >
                        <Heart className="w-4 h-4 text-primary-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{service}</span>
                      </motion.div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Contact Info */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h5 className="font-semibold text-warm-200 text-lg mb-6">
                  Contact
                </h5>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Phone className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <a href="tel:0621306856" className="hover:text-primary-300 transition-colors">
                        <p className="text-sage-300 text-sm">06 21 30 68 56</p>
                      </a>
                      <p className="text-sage-400 text-xs">Lun-Ven 09h-16h</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <a href="mailto:angelerosalie.sf@proton.me" className="hover:text-primary-300 transition-colors">
                        <p className="text-sage-300 text-sm">angelerosalie.sf@proton.me</p>
                      </a>
                      <p className="text-sage-400 text-xs">Réponse sous 48h ouvrées</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sage-300 text-sm">Annecy et alentours</p>
                      <p className="text-sage-400 text-xs">Rayon de 25 km</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sage-300 text-sm">Urgences 24h/7j</p>
                      <p className="text-sage-400 text-xs">Disponible pour vous</p>
                    </div>
                  </div>
                </div>

                
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-sage-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-sage-400 text-sm text-center md:text-left"
            >
              <p>
                © {currentYear} Angèle Rosalie - Sage-femme libérale. N°RPPS 10100156131.
              </p>
              {/* Numéros administratifs non affichés */}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="flex space-x-6"
            >
              {legalLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  aria-label={`Lire les ${link.name}`}
                  className="text-sage-400 hover:text-primary-400 text-sm transition-colors duration-200"
                >
                  {link.name}
                </a>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
